import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Mail } from "lucide-react";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import { getBrandHost, postLoginPath, goPostLogin, safeLoginDestination } from "@/lib/moduleHost";
import AppleSignInButton from "@/components/auth/AppleSignInButton";
import { PasskeySignInButton } from "@/components/auth/PasskeyButtons";
import AuthBlockedNotice from "@/components/auth/AuthBlockedNotice";
import { isDemoMode, demoModuleKey } from "@/lib/demoMode";

/** Handle the four LoginPage post-login destinations uniformly.
 *  `dest` may be:
 *    • an in-app path ("/account", "/agency/settings")  → react-router navigate
 *    • an absolute URL ("https://pwa.hustlesuite.au/")  → full page redirect
 */

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function LoginPage() {
  const { t } = useTranslation();
  const [loginCopy, setLoginCopy] = useState({
    header_text: getBrandHost()?.title || "HustleSuite",
    tagline: "Track your orders and save your dietary preferences. Staff sign in here too.",
    footer_text: "",
  });
  useEffect(() => {
    const mod = getBrandHost();
    if (!mod) return;
    setLoginCopy({ header_text: mod.title, tagline: mod.desc, footer_text: "" });
    axios.get(`${API}/api/login-page/${mod.key}`)
      .then(({ data }) => data?.header_text && setLoginCopy(data))
      .catch(() => {});
  }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberComputer, setRememberComputer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [blockDetail, setBlockDetail] = useState(null);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const isDemo = isDemoMode();
  const { login, refresh, user, checking } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Honour ?returnTo=… so deep-links (Matchmaker quiz, cart, etc.)
  // land users back where they came from after signin. Only allow
  // in-app paths — never an external URL.
  const returnTo=safeLoginDestination(searchParams.get("returnTo")) || safeLoginDestination(searchParams.get("next"));
  useEffect(()=>{
    if(!checking && user && !submitting && !codeVerifying) goPostLogin(navigate,returnTo || postLoginPath(user.role));
  },[user,checking,returnTo,submitting,codeVerifying,navigate]);
  const sessionHandled = useRef(false);

  // ── Emergent Google Auth callback capture ──────────────────────
  // After the Google round-trip, we land back on /login#session_id=…
  // Detect the fragment SYNCHRONOUSLY on mount, exchange it via our
  // backend, then redirect to the appropriate role landing page.
  useEffect(() => {
    if (sessionHandled.current) return;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    sessionHandled.current = true;
    const sid = decodeURIComponent(m[1]);
    // Strip the fragment so a page-refresh doesn't retry the exchange.
    window.history.replaceState(null, "", window.location.pathname
                                              + window.location.search);
    (async () => {
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`,
          { session_id: sid },
          { withCredentials: true });
        if (typeof refresh === "function") {
          const confirmed=await refresh();
          if(!confirmed)throw Error("Sign-in succeeded but the session was not saved. Please allow site cookies and try again.");
        }
        toast.success(`${t("login_welcome_prefix")}${data?.name || data?.email}`);
        const role = data?.role || "customer";
        goPostLogin(navigate, returnTo || postLoginPath(role));
      } catch (err) {
        toast.error(err?.response?.data?.detail
                      || t("login_google_failed"));
      }
    })();
  }, [navigate, refresh]);

  async function requestMagicLink() {
    if (!email) {
      toast.error(t("login_enter_email_first"));
      return;
    }
    setMagicSending(true);
    try {
      await axios.post(`${API}/api/auth/magic-link/request`,
                        { email: email.trim().toLowerCase(), remember_computer: rememberComputer },
                        { withCredentials: true });
      setMagicSent(true);
      toast.success(t("login_magic_toast_sent"));
    } catch (_) {
      toast.error(t("login_magic_toast_failed"));
    }
    setMagicSending(false);
  }

  async function requestEmailCode() {
    if (!email) {
      toast.error(t("login_enter_email_first"));
      return;
    }
    setCodeSending(true);
    try {
      await axios.post(`${API}/api/auth/email-code/request`,
                        { email: email.trim().toLowerCase(), remember_computer: rememberComputer },
                        { withCredentials: true });
      setCode("");
      toast.success(codeSent
        ? "New code sent — enter the one from the latest email."
        : "Code sent — check your email.");
      setCodeSent(true);
    } catch (_) {
      toast.error("Couldn't send the code — try again shortly.");
    }
    setCodeSending(false);
  }

  async function verifyEmailCode() {
    if (!code.trim()) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setCodeVerifying(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/email-code/verify`,
        { email: email.trim().toLowerCase(), code: code.trim(), remember_computer: rememberComputer },
        { withCredentials: true });
      if (typeof refresh === "function") {
          const confirmed=await refresh();
          if(!confirmed)throw Error("Sign-in succeeded but the session was not saved. Please allow site cookies and try again.");
        }
      toast.success(`${t("login_welcome_prefix", { defaultValue: "Welcome, " })}${data?.name || data?.email}`);
      const role = data?.role || "customer";
      goPostLogin(navigate, returnTo || postLoginPath(role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Invalid or expired code.");
    }
    setCodeVerifying(false);
  }

  async function signInAsDemo() {
    setDemoSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/auth/demo-login`,
        { module: demoModuleKey() },
        { withCredentials: true });
      if (typeof refresh === "function") {
          const confirmed=await refresh();
          if(!confirmed)throw Error("Sign-in succeeded but the session was not saved. Please allow site cookies and try again.");
        }
      toast.success(`${t("login_welcome_prefix", { defaultValue: "Welcome, " })}${res.data?.name || "demo user"}`);
      const role = res.data?.role || "customer";
      goPostLogin(navigate, returnTo || postLoginPath(role));
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || t("login_demo_failed", { defaultValue: "Demo login failed — try again shortly." }));
    } finally {
      setDemoSubmitting(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password, rememberComputer);
    setSubmitting(false);
    if (res.ok) {
      setBlockDetail(null);
      toast.success(t("login_welcome_back"));
      const role = res.user?.role || "customer";
      goPostLogin(navigate, returnTo || postLoginPath(role));
    } else if (res.blockDetail) {
      setBlockDetail(res.blockDetail);
    } else {
      setBlockDetail(null);
      toast.error(res.error);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] dark:bg-stone-950 px-4 py-12">
      {/* iter316 — NO local LanguagePicker, NO local Back. The black
       *  top global bar (GoogleTranslateBar) carries the "← Back to
       *  {Module}" chip + Language + Theme + Modules dropdown for
       *  every page in the Suite, /admin/login included. */}
      <div
        data-testid="login-card"
        className="w-full max-w-md bg-white rounded-2xl border border-[#E8E5DF] p-8 md:p-10"
      >
        <Link
          to="/"
          data-testid="login-back-home"
          className="font-serif-display text-2xl text-[#121614] hover:text-[#C05746]"
        >
          {loginCopy.header_text}
        </Link>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-6">{t("login_heading")}</h1>
        <p className="text-sm text-[#5C625E] mt-2" data-testid="login-tagline">
          {loginCopy.tagline}
        </p>

        {blockDetail ? (
          <div className="mt-6">
            <AuthBlockedNotice
              detail={blockDetail}
              onRetry={() => setBlockDetail(null)}
            />
          </div>
        ) : null}

        {isDemo ? (
          <div
            data-testid="login-demo-card"
            className="mt-6 p-4 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-700">
              Demo mode · {demoModuleKey()}
            </p>
            <p className="mt-2 text-sm text-amber-900 leading-relaxed">
              Skip the form. One click and you land in a fully-populated
              demo account with sample data. Resets nightly. Nothing you
              do here is billed.
            </p>
            <Button
              type="button"
              onClick={signInAsDemo}
              disabled={demoSubmitting}
              data-testid="login-demo-button"
              className="mt-3 w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full h-11 gap-2 font-bold"
            >
              <Sparkles size={16} />
              {demoSubmitting ? "Signing in…" : "Sign in as demo user"}
            </Button>
          </div>
        ) : null}

        <div className="mt-6">
          <label className="flex items-center gap-3 text-sm text-[#121614]" htmlFor="login-remember">
            <input id="login-remember" data-testid="login-remember-computer" type="checkbox"
              checked={rememberComputer} onChange={(e) => setRememberComputer(e.target.checked)}
              className="h-4 w-4" />
            Remember this computer for 30 days
          </label>
          <p className="mt-2 text-xs text-[#5C625E]">For password, email-code and sign-in-link login. Use only on a private computer. Signing out clears this browser’s session.</p>
        </div>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="login-email">{t("login_email_label")}</Label>
            <Input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">{t("login_password_label")}</Label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-xs text-[#C05746] hover:underline"
              >
                {t("login_forgot_password")}
              </Link>
            </div>
            <Input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="w-full bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-12"
          >
            {submitting ? t("login_signing_in") : t("login_submit")}
          </Button>
        </form>

        {/* ── Passwordless: magic-link sign-in ────────────────── */}
        <div className="mt-6 pt-6 border-t border-[#E8E5DF]">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wider text-[#5C625E]">
              {t("login_or_no_password")}
            </p>
            <Sparkles size={14} className="text-[#C05746]"/>
          </div>
          {magicSent ? (
            <p className="text-sm text-[#121614] bg-[#F7F3E8] p-3 rounded-xl"
                data-testid="login-magic-sent">
              {t("login_magic_sent_body", { email })}
            </p>
          ) : (
            <Button
              type="button"
              onClick={requestMagicLink}
              disabled={magicSending}
              data-testid="login-magic-link-button"
              className="w-full bg-[#121614] hover:bg-[#2C3448] text-[#FDFBF7] rounded-full h-12 gap-2"
            >
              <Mail size={16}/>
              {magicSending ? t("login_magic_sending") : t("login_magic_button")}
            </Button>
          )}

          {/* Email me a 6-digit code (passwordless OTP) */}
          <div className="mt-3">
            {codeSent ? (
              <div className="space-y-2" data-testid="login-code-form">
                <Input
                  data-testid="login-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="rounded-xl h-11 text-center tracking-[0.5em] font-bold"
                />
                <Button
                  type="button"
                  onClick={verifyEmailCode}
                  disabled={codeVerifying}
                  data-testid="login-code-verify-button"
                  className="w-full bg-[#121614] hover:bg-[#2C3448] text-[#FDFBF7] rounded-full h-12"
                >
                  {codeVerifying ? "Verifying…" : "Verify & sign in"}
                </Button>
                <button
                  type="button"
                  onClick={requestEmailCode}
                  disabled={codeSending}
                  data-testid="login-code-resend"
                  className="w-full text-xs text-[#5C625E] hover:text-[#C05746]"
                >
                  {codeSending ? "Sending…" : "Resend code"}
                </button>
              </div>
            ) : (
              <Button
                type="button"
                onClick={requestEmailCode}
                disabled={codeSending}
                data-testid="login-code-button"
                className="w-full bg-white hover:bg-[#F7F3E8] text-[#121614] border border-[#121614] rounded-full h-12 gap-2"
              >
                <Mail size={16}/>
                {codeSending ? "Sending code…" : "Email me a 6-digit code"}
              </Button>
            )}
          </div>

          {/* Sign in with Google (Emergent-managed) */}
          <div className="mt-3">
            <GoogleSignInButton landingPath="/account" />
          </div>

          {/* Sign in with Apple */}
          <div className="mt-3">
            <AppleSignInButton />
          </div>

          {/* Sign in with a passkey / Face ID / Touch ID */}
          <div className="mt-3">
            <PasskeySignInButton
              email={email}
              onSuccess={(user) => {
                const role = user?.role || "customer";
                goPostLogin(navigate, postLoginPath(role));
              }}
            />
          </div>
        </div>

        <p className="mt-6 text-xs text-[#5C625E] text-center">
          {t("login_new_here")}{" "}
          <Link
            to="/register"
            data-testid="login-register-link"
            className="text-[#C05746] hover:text-[#A64A3C] font-medium"
          >
            {t("login_create_account")}
          </Link>
        </p>
      </div>
    </div>
  );
}
